import { useCallback, useMemo } from 'react';
import { PROFILE_MANUAL_MARK_KIND, type ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { useMarkProfileAssetMutation } from '../../../../../redux/api/fymAPI';
import { profileSlotOf } from '../profileSlots';
import { HAND_SERVICES, type HandService } from './handServices';

export interface HandRow {
  service: HandService;
  /** The file to download: the current artist photo. */
  downloadAsset: ReturnType<typeof profileSlotOf>['current'];
  /** When the artist marked it set, on the current artist photo; null when not set. */
  setAt: string | null;
}

/**
 * The "I set it" marks of "Set these by hand" (design c2). The server stores them on the current
 * version of the artist photo (`POST .../assets/:uuid/mark { set, service }`, `manualMarks`), so
 * they reach every device, the API and MCP; a new version starts unmarked (design c2 note). The
 * event `profile.asset.updated` reads the Profile again after a mark.
 */
export function useHandMarks(accountId: number, profile: Pick<ArtistProfileResponse, 'assets'>) {
  const [mark, markState] = useMarkProfileAssetMutation();
  const photo = profileSlotOf(profile.assets, PROFILE_MANUAL_MARK_KIND).current;

  const rows = useMemo<HandRow[]>(
    () =>
      HAND_SERVICES.map((service) => ({
        service,
        downloadAsset: photo,
        setAt: photo?.manualMarks?.[service] ?? null,
      })),
    [photo]
  );

  const toggle = useCallback(
    (row: HandRow) => {
      if (photo === null) {
        return;
      }
      mark({ accountId, assetUuid: photo.uuid, set: row.setAt === null, service: row.service })
        .unwrap()
        .catch(console.error);
    },
    [accountId, mark, photo]
  );

  const setCount = useMemo(() => rows.filter((row) => row.setAt !== null).length, [rows]);
  return { rows, toggle, setCount, isMarking: markState.isLoading, hasPhoto: photo !== null };
}
